import { max, integer } from '@vee-validate/rules';
import { defineRule } from 'vee-validate';

defineRule('existing-sku-or-barcode', (value, args) => {
    const findSku = Array.isArray(args) ? args[0] : args?.isExistingSku;
    const findBarcode = Array.isArray(args) ? args[1] : args?.isExistingBarcode;
    const matches = (typeof findSku === 'function' && findSku(value))
        || (typeof findBarcode === 'function' && findBarcode(value));
    return matches || 'Value does not match any existing product SKU or barcode.';
});

export default {
    data() {
        return {
            arTransactionsConfiguration: {
                type: 'arTransactions',
                label: 'AR Transactions',
                sampleNotes: [
                    'All fields required except Location',
                    'Location is optional; if provided, it must match an existing location in Mosaic',
                    'AR Account Code and Account Number must already be present in Mosaic',
                    'SKU or barcode must match an existing product SKU or barcode in Mosaic',
                    'Quantity must not be 0',
                    'If the Customer is not currently linked to the AR Account check the Link checkbox in the Warning section to link them during import, otherwise the record will fail to import',
                    'The AR Account must have a POS Tender associated with it. If it does not the record will not import'
                ],
                fields: [
                    {
                        key: 'LinkCustomerToArAccount',
                        label: 'Link',
                        inputType: 'checkbox',
                        omitFromCsv: true,
                        warningMessage: 'Customer is not linked to AR account.',
                        hasWarningCalculator: (value, row) => {
                            return this.isCustomerLinkedToArAccount(row);
                        },
                    },
                    {
                        key: 'ARAccountCode',
                        label: 'AR Account Code',
                        hasErrorCalculator: value => { 
                            return (!this.matchesExistingObject(value, true));
                        },
                        rules: 'min_value:0|required',
                        comparisonProperty: 'number',
                        dataType: 'accounts',
                        samples: [
                            '"PELL"',
                            '"Susan Smith Freshman Fund"',
                            '"ALUMNI"'
                        ]
                    },
                    {
                        key: 'AccountNumber',
                        label: 'Account Number',
                        hasErrorCalculator: value => {
                            return (!this.matchesExistingObject(value, true));
                        },
                        warningDefault: null,
                        rules: 'min_value:0|required',
                        dataType: 'customers',
                        comparisonProperty: 'number',                        
                        samples: ['"Cust-0040518"', '"Cust-0040519"', '"Cust-1241966"']
                    }, 
                    {
                        key: 'SkuOrBarcode',
                        label: 'SKU or Barcode',
                        hasErrorCalculator: value => {
                            if (this.isEmpty(value)) {
                                return true;
                            }
                            if (max(value, { length: 128 }) !== true) {
                                return true;
                            }
                            return !this.isExistingSku(value) && !this.isExistingBarcode(value);
                        },
                        rules: {
                            required: true,
                            max: { length: 128 },
                            'existing-sku-or-barcode': {
                                isExistingSku: this.isExistingSku,
                                isExistingBarcode: this.isExistingBarcode
                            }
                        },
                        samples: ['"50000012"', '"2235467890161"', '"50000014"']
                    },
                    {
                        key: 'Quantity',
                        label: 'Quantity',
                        hasErrorCalculator: value => {
                            if (this.isEmpty(value)) {
                                return true;
                            }
                            if (!integer(value)) {
                                return true;
                            }

                            return value === 0 || value === '0';
                        },
                        rules: 'required',
                        samples: [1, 5, 10, 15],
                        convertToNumber: true
                    },
                    {
                        key: 'Location',
                        label: 'Location',
                        hasErrorCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        rules: 'min_value:0',
                        samples: ['"North Campus"', '"City Campus"', '"South Annex"'],
                        dataType: 'locations'
                    },                    
                ]
            }
        };
    },
    methods: {
        isExistingBarcode(value) {
            if (this.isEmpty(value)) {
                return false;
            }
            return this.existingBarcodes.has((value + '').toLowerCase());
        },
        isCustomerLinkedToArAccount(row) {
            const customerId = row.AccountNumberId;
            const arAccountId = row.ARAccountCodeId;

            if (!customerId || customerId === -1 || !arAccountId || arAccountId === -1) {
                return false;
            }

            if (row.LinkCustomerToArAccount === true) {
                if (!this.customerArAccounts) {
                    this.customerArAccounts = [];
                }

                const alreadyLinked = this.customerArAccounts.some(link => link.customerId === customerId && link.accountId === arAccountId);

                if (!alreadyLinked) {
                    this.customerArAccounts.push({ customerId, accountId: arAccountId });
                }

                return false;
            }

            return !this.customerArAccounts.some(link => link.customerId === customerId && link.accountId === arAccountId);
        }
    }
};
